import { type Accessor, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { createExtendedPowerSyncStatus } from "@journal/shared/solid";

/**
 * Resolution of a detail entity in a local-first app.
 *
 * The problem: when opening `/entry/$entryId`, the row may:
 *   - exist locally → show it;
 *   - **not be synced yet** (initial sync in progress) → keep loading;
 *   - **not exist** (deleted / invalid id) → "not found";
 *   - **not be accessible** (PowerSync sync rules do NOT deliver rows without
 *     permission, so from the client "no permission" ≈ "absent after sync") → "no access".
 *
 * Without this handling, an absent entity leaves the screen **loading forever**.
 * Here we distinguish the states to give correct feedback.
 */
type EntityResolution = "loading" | "ready" | "not-found" | "offline";

export interface ResolutionInputs {
  /** The entity is present locally. */
  hasEntity: boolean;
  /** The query has not resolved yet (no settled result yet). */
  isPending: boolean;
  /** PowerSync completed the initial sync. */
  hasSynced: boolean;
  /** There is a network connection. */
  isOnline: boolean;
  /** The safety net timed out while waiting (prevents loading forever). */
  timedOut: boolean;
}

/**
 * PURE, testable core of the state machine (no reactivity).
 *
 * - present → `ready`
 * - query pending → `loading`
 * - settled and absent:
 *     - already synced (or timeout elapsed) → `not-found` (does not exist / no access)
 *     - offline and never synced → `offline` (cannot know yet)
 *     - online but initial sync still pending → `loading` (wait for delivery)
 */
export function resolveEntityState(input: ResolutionInputs): EntityResolution {
  if (input.hasEntity) {
    return "ready";
  }
  if (input.isPending) {
    return "loading";
  }
  if (input.hasSynced || input.timedOut) {
    return "not-found";
  }
  if (!input.isOnline) {
    return "offline";
  }
  return "loading";
}

const DEFAULT_RESOLUTION_TIMEOUT_MS = 8000;

interface EntityResolutionOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  /** `true` when the entity is loaded locally. */
  hasEntity: Accessor<boolean>;
  /** `true` while the query has no settled result. */
  isPending: Accessor<boolean>;
  /** Safety net: ms waiting (online, no sync) before declaring not-found. */
  timeoutMs?: number;
}

/**
 * Reactive wrapper: combines the entity query with PowerSync's sync state
 * and a timeout safety net. Returns the resolution state.
 */
export function createEntityResolution(
  options: EntityResolutionOptions,
): Accessor<EntityResolution> {
  const status = createExtendedPowerSyncStatus(options.db);
  const [timedOut, setTimedOut] = createSignal(false);

  // Starts the timeout only during the real waiting window (absent, online,
  // no initial sync). If the entity arrives or the state changes, it is cancelled/restarted.
  createEffect(() => {
    const waiting =
      !options.hasEntity() && !options.isPending() && !status().hasSynced && status().isOnline;
    if (!waiting) {
      setTimedOut(false);
      return;
    }
    const id = setTimeout(() => {
      setTimedOut(true);
    }, options.timeoutMs ?? DEFAULT_RESOLUTION_TIMEOUT_MS);
    onCleanup(() => {
      clearTimeout(id);
    });
  });

  const resolution = createMemo(() =>
    resolveEntityState({
      hasEntity: options.hasEntity(),
      isPending: options.isPending(),
      hasSynced: status().hasSynced,
      isOnline: status().isOnline,
      timedOut: timedOut(),
    }),
  );
  return resolution;
}
